'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Composed per-turn AI chat runtime store (FUN-4558). Merges three slices that
 * are all part of the SAME "current turn lifecycle" concern and are read
 * together across the chat panel + grid:
 *
 * - `inFlightThreadId` — the thread the first message of a new chat is running
 *   on while the URL `?chat=` is still empty (it only flips after `sendMessage`
 *   resolves). The grid reads it to key its sessionStorage under the right chat.
 * - `pendingPrompt` — a prompt stashed from the `?prompt=` URL param that STARTS
 *   a new chat once a panel mounts.
 * - `followup` — a follow-up routed into the CURRENT thread (e.g. the grid's
 *   "show 10 more relevant" action), keeping the existing search as context.
 *
 * Persisted/session stores (`activity-ai-chat-store`, the sessionStorage
 * id/score stores) stay separate — different lifetime and concern.
 */

type ActivityAiChatRuntimeState = {
  inFlightThreadId: string | null;
  pendingPrompt: string;
  followup: string;
  setInFlightThreadId: (id: string | null) => void;
  setPendingPrompt: (prompt: string) => void;
  setFollowup: (message: string) => void;
};
export const useActivityAiChatRuntimeStore = create<ActivityAiChatRuntimeState>(set => ({
  inFlightThreadId: null,
  pendingPrompt: '',
  followup: '',
  setInFlightThreadId: id => set(state => state.inFlightThreadId === id ? state : {
    inFlightThreadId: id
  }),
  setPendingPrompt: prompt => set({
    pendingPrompt: prompt
  }),
  setFollowup: message => set({
    followup: message
  })
}));

// --- In-flight thread id ---------------------------------------------------

export function setInFlightThreadId(id: string | null): void {
  useActivityAiChatRuntimeStore.getState().setInFlightThreadId(id);
}
export function useInFlightThreadId() {
  return useActivityAiChatRuntimeStore(_temp);
}

// --- Pending prompt (starts a NEW chat) ------------------------------------

/** Stash a prompt (from the `?prompt=` URL param) to start a new chat. */
function _temp(state) {
  return state.inFlightThreadId;
}
export function setPendingPrompt(prompt: string): void {
  useActivityAiChatRuntimeStore.getState().setPendingPrompt(prompt);
}

/** Read the pending prompt and clear it atomically. */
export function consumePendingPrompt(): string {
  const {
    pendingPrompt
  } = useActivityAiChatRuntimeStore.getState();
  if (pendingPrompt.length > 0) useActivityAiChatRuntimeStore.setState({
    pendingPrompt: ''
  });
  return pendingPrompt;
}

/**
 * Reactively dispatch a pending prompt (set by `ActivityAiAutoPrompt` from the
 * `?prompt=` URL param) exactly once. Subscribing to the store — instead of
 * reading it in a lazy `useState` initializer — removes the mount-order race
 * between the writer and the panel. `consumePendingPrompt` clears the store
 * atomically, so when several chat panels are mounted at once (desktop sidebar
 * + mobile drawer) only the first effect to run fires `onStart`.
 */
export function usePendingPromptAutoStart(onStart) {
  const $ = _c(5);
  const prompt = useActivityAiChatRuntimeStore(_temp2);
  let t0;
  if ($[0] !== onStart || $[1] !== prompt) {
    t0 = () => {
      if (prompt.trim().length === 0) {
        return;
      }
      const text = consumePendingPrompt().trim();
      if (text.length === 0) {
        return;
      }
      onStart(text);
    };
    $[0] = onStart;
    $[1] = prompt;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== prompt) {
    t1 = [prompt];
    $[3] = prompt;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
}

// --- Follow-up (sent into the CURRENT thread) ------------------------------

/** Set a follow-up to be dispatched into the active thread. */
function _temp2(state) {
  return state.pendingPrompt;
}
export function setFollowup(message: string): void {
  useActivityAiChatRuntimeStore.getState().setFollowup(message);
}

/** The tool clamps the resulting `limit`, so repeated taps stay bounded. */
export const SHOW_MORE_BATCH_SIZE = 50;

/**
 * Carries an explicit TARGET count rather than "show me more", so the model sets
 * the tool `limit` deterministically instead of guessing.
 */
export function buildShowMorePrompt(translate: (id: string, values: {
  count: number;
}, fallback: {
  message: string;
}) => string, currentActivityCount: number): string {
  return translate('activity.search.ai.results.showMorePrompt', {
    count: currentActivityCount + SHOW_MORE_BATCH_SIZE
  }, {
    message: 'Show me the {count} most relevant activities'
  });
}

/** Read the pending follow-up and clear it atomically. */
export function consumeFollowup(): string {
  const {
    followup
  } = useActivityAiChatRuntimeStore.getState();
  if (followup.length > 0) useActivityAiChatRuntimeStore.setState({
    followup: ''
  });
  return followup;
}

/**
 * Reactively dispatch a pending follow-up into the active thread exactly once.
 * Subscribing to the store (not a lazy initializer) removes the mount-order race
 * between the grid writer and the panel. `consumeFollowup` clears atomically, so
 * when several chat panels are mounted at once (desktop sidebar + mobile drawer)
 * only the first effect to run fires `onSend`. Mirrors `usePendingPromptAutoStart`.
 */
export function useFollowupAutoSend(onSend) {
  const $ = _c(5);
  const message = useActivityAiChatRuntimeStore(_temp3);
  let t0;
  if ($[0] !== message || $[1] !== onSend) {
    t0 = () => {
      if (message.trim().length === 0) {
        return;
      }
      const text = consumeFollowup().trim();
      if (text.length === 0) {
        return;
      }
      onSend(text);
    };
    $[0] = message;
    $[1] = onSend;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== message) {
    t1 = [message];
    $[3] = message;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
}
function _temp3(state) {
  return state.followup;
}